import { Formik, useField } from 'formik';
import {
  CompanyPageCompanyFragment,
  useCompanyPageUpdateCompanyMutation,
} from 'generated/graphql';
import {
  enableDisableRadioButtonOptions,
  EnableDisableValue,
} from 'views/Company/CompanyView/CompanyView.helpers';
import {
  EditFormWrapper,
  FormikRadioButtonGroup,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  HIRING_TEAM_MEMBER_ADDED_EMAIL_LABEL,
  NOTIFICATIONS_DATA_TEST_ID,
  ROLE_LIVE_EMAIL_LABEL,
} from './NotificationsSection.constants';
import styles from './NotificationsSection.module.scss';

type NotificationsSectionFormValues = {
  roleLiveEmail: string;
  hiringTeamMemberAddedEmail: string;
};

type NotificationsSectionFormProps = {
  company: CompanyPageCompanyFragment;
  onCancelClick: () => void;
  onSave: () => void;
};

const NotificationsSectionForm = ({
  company,
  onCancelClick,
  onSave,
}: NotificationsSectionFormProps) => {
  const { handleMsgType } = useNotification();

  const [editCompanyMutation] = useCompanyPageUpdateCompanyMutation();

  const handleSave = (values: NotificationsSectionFormValues) => {
    editCompanyMutation({
      variables: {
        id: company.id,
        name: company.name,
        isRoleLiveEmailEnabled:
          values.roleLiveEmail === EnableDisableValue.Enabled,
        isHiringTeamMemberAddedEmailEnabled:
          values.hiringTeamMemberAddedEmail === EnableDisableValue.Enabled,
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          title: 'Notification settings updated.',
          dismissable: true,
        });
        onSave();
      },
      onError: () => {
        handleMsgType({
          type: TNotification.error,
          title: 'Notification settings could not be updated',
          message: 'Something went wrong. Please try again.',
          dismissable: true,
        });
      },
    });
  };

  const initialValues = {
    roleLiveEmail: company.isRoleLiveEmailEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
    hiringTeamMemberAddedEmail: company.isHiringTeamMemberAddedEmailEnabled
      ? EnableDisableValue.Enabled
      : EnableDisableValue.Disabled,
  };

  return (
    <Formik<NotificationsSectionFormValues>
      initialValues={initialValues}
      onSubmit={handleSave}
    >
      {({ submitForm }) => (
        <EditFormWrapper
          onCancelClick={onCancelClick}
          onSaveClick={submitForm}
          title="Notifications"
          formId="notifications-form"
          hasResetAllButton={false}
          data-testid={NOTIFICATIONS_DATA_TEST_ID}
        >
          <form
            onSubmit={event => event.preventDefault()}
            id="notifications-form-element"
            className={styles.form}
          >
            <FormikRadioButtonGroup
              id="roleLiveEmail"
              name="roleLiveEmail"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label={ROLE_LIVE_EMAIL_LABEL}
              data-testid={`${NOTIFICATIONS_DATA_TEST_ID}__role-live-email-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
            <FormikRadioButtonGroup
              id="hiringTeamMemberAddedEmail"
              name="hiringTeamMemberAddedEmail"
              options={enableDisableRadioButtonOptions}
              layout="horizontal"
              label={HIRING_TEAM_MEMBER_ADDED_EMAIL_LABEL}
              data-testid={`${NOTIFICATIONS_DATA_TEST_ID}__hiring-team-member-added-email-radio-group`}
              useFormikField={useField}
              className={styles.radioGroup}
            />
          </form>
        </EditFormWrapper>
      )}
    </Formik>
  );
};

export default NotificationsSectionForm;
